import { useMemo, useRef, useState } from "react";
import useSWR from "swr";
import moment from "moment/moment";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import ModalDialog, {
  ModalDialogBody,
  ModalDialogFooter,
} from "../UI/ModalDialog";
import Accordion, { AccordionItem } from "../UI/Accordion";
import Button from "../UI/Button";
import Callout from "../UI/Callout";
import CheckboxStandalone from "../UI/CheckboxStandalone";
import IconButton from "../UI/IconButton";
import SearchInput from "../UI/SearchInput";
import Status from "../UI/Status";
import Text from "../UI/Text";
// @ts-expect-error pure js
import TextInputStandalone from "../UI/TextInputStandalone";
// @ts-expect-error pure js
import RadioStandalone from "../UI/RadioStandalone";
// @ts-expect-error pure js
import useAuthenticatedAxios from "../../hooks/use-authenticated-axios";
// @ts-expect-error pure js
import useLoader from "../../hooks/use-loader";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
import DocumentDownloadLink from "../UI/DocumentDownloadLink";
// @ts-expect-error pure js
import DocumentThumbnail from "../UI/DocumentThumbnail";
import type { DownloadableFile } from "../../hooks/use-document-download";
// @ts-expect-error pure js
import useProperties from "../../hooks/use-properties";
import type { Property } from "../../models/property";
import type { PropertyFolder } from "../../models/propertyFolder";
import type { CharteBlock, CharteClause } from "../../models/charteColocation";
import styles from "./CharteColocationForm.module.css";

interface CustomArticle {
  key: number;
  title: string;
  body: string;
}

interface CharteColocationFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Dossier de la colocation (ses chambres sont les biens). */
  propertyFolder: PropertyFolder;
  /** Appelé une fois le PDF rangé dans les documents de la colocation. */
  onGenerated?: () => void;
}

const CLAUSES_URL = "/property-folders/charte-colocation/clauses";

function formatAddress(place: Pick<PropertyFolder, "rue" | "cp" | "ville">) {
  const city = [place.cp, place.ville].filter(Boolean).join(" ");
  return [place.rue, city].filter(Boolean).join(", ");
}

/** Adresse du dossier, sinon celle d'une de ses chambres : elles partagent la même. */
function knownAddress(folder: PropertyFolder, properties: Property[]) {
  const own = formatAddress(folder);
  if (own) return own;
  const room = properties.find(
    (candidate) => candidate.id_property_folder === folder.id && candidate.rue,
  );
  return room ? formatAddress(room) : "";
}

/**
 * Composition de la charte d'une colocation : clauses du catalogue cochées
 * thème par thème, articles libres, puis génération du PDF rangé dans les
 * documents de la colocation et partagé avec les colocataires de ses chambres.
 */
export default function CharteColocationForm({
  open,
  onOpenChange,
  propertyFolder,
  onGenerated,
}: CharteColocationFormProps) {
  const axios = useAuthenticatedAxios();
  const { data: blocks = [], isLoading } = useSWR<CharteBlock[]>(
    open ? CLAUSES_URL : null,
    (url: string) =>
      axios.get(url).then((response: { data: CharteBlock[] }) => response.data),
  );

  const { properties } = useProperties() as { properties: Property[] };
  const resolvedAddress = knownAddress(propertyFolder, properties);
  const [addressInput, setAddressInput] = useState("");
  const address = resolvedAddress || addressInput;
  const [date, setDate] = useState(() => moment().format("YYYY-MM-DD"));
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const [variants, setVariants] = useState<Record<string, string>>({});
  const [articles, setArticles] = useState<CustomArticle[]>([]);
  const [search, setSearch] = useState("");
  const [openBlocks, setOpenBlocks] = useState<(string | number)[]>([1]);
  const [error, setError] = useState("");
  const [document, setDocument] = useState<
    (DownloadableFile & { id: number }) | null
  >(null);
  const nextArticleKey = useRef(1);

  const isChecked = (clause: CharteClause) =>
    overrides[clause.id] ?? clause.default_checked;

  const filledArticles = articles.filter(
    (article) => article.title.trim() || article.body.trim(),
  );
  const selectedCount =
    blocks.reduce(
      (count, block) => count + block.clauses.filter(isChecked).length,
      0,
    ) + filledArticles.length;

  const query = search.trim().toLowerCase();
  const visibleBlocks = useMemo(
    () =>
      blocks
        .map((block) => ({
          ...block,
          clauses: query
            ? block.clauses.filter((clause) =>
                `${clause.title} ${clause.description}`
                  .toLowerCase()
                  .includes(query),
              )
            : block.clauses,
        }))
        .filter((block) => block.clauses.length > 0),
    [blocks, query],
  );

  function reset() {
    setAddressInput("");
    setDate(moment().format("YYYY-MM-DD"));
    setOverrides({});
    setVariants({});
    setArticles([]);
    setSearch("");
    setOpenBlocks([1]);
    setError("");
    setDocument(null);
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) reset();
    onOpenChange(nextOpen);
  }

  function addArticle() {
    setArticles((current) => [
      ...current,
      { key: nextArticleKey.current++, title: "", body: "" },
    ]);
  }

  function updateArticle(key: number, values: Partial<CustomArticle>) {
    setArticles((current) =>
      current.map((article) =>
        article.key === key ? { ...article, ...values } : article,
      ),
    );
  }

  const [isGenerating, handleGenerate] = useLoader(async () => {
    try {
      setError("");
      const clauses = blocks.flatMap((block) =>
        block.clauses.filter(isChecked).map((clause) => ({
          id: clause.id,
          variant: clause.variants.length
            ? variants[clause.id] ?? clause.default_variant
            : null,
        })),
      );
      const response = await axios.post(
        `/property-folders/${propertyFolder.id}/charte-colocation`,
        {
          address: address.trim(),
          date,
          clauses,
          articles: filledArticles.map(({ title, body }) => ({ title, body })),
        },
      );
      setDocument(response.data);
      onGenerated?.();
    } catch (err) {
      setError(handleAPIError(err));
      throw err;
    }
  });

  const canGenerate =
    !isLoading && address.trim() !== "" && date !== "" && selectedCount > 0;

  return (
    <ModalDialog
      open={open}
      onOpenChange={handleOpenChange}
      size="lg"
      header="Charte de colocation"
      subtitle={propertyFolder.nom}
    >
      {document ? (
        <>
          <ModalDialogBody className={styles.body}>
            <Callout variant="success" title="Charte générée">
              La charte a été ajoutée aux documents de la colocation et partagée
              avec les colocataires. Imprimez-la et faites-la signer par chacun
              d'eux.
            </Callout>
            <div className={styles.preview}>
              <DocumentDownloadLink documentId={document.id} file={document}>
                <DocumentThumbnail documentId={document.id} file={document} />
              </DocumentDownloadLink>
            </div>
          </ModalDialogBody>
          <ModalDialogFooter>
            <Button type="button" onClick={() => handleOpenChange(false)}>
              Fermer
            </Button>
          </ModalDialogFooter>
        </>
      ) : (
        <>
          <ModalDialogBody className={styles.body}>
            <Text>
              Cochez les clauses qui concernent votre colocation, ajoutez vos
              propres règles, puis générez le document à faire signer par les
              colocataires. Chaque clause reste conforme : aucune pénalité,
              aucune interdiction abusive.
            </Text>
            <div className={styles.fields}>
              {resolvedAddress ? (
                <div className={styles.addressField}>
                  <Text variant="deci">Adresse du logement</Text>
                  <Text>{resolvedAddress}</Text>
                </div>
              ) : (
                <TextInputStandalone
                  label="Adresse du logement"
                  name="address"
                  value={addressInput}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setAddressInput(e.target.value)
                  }
                  className={styles.addressField}
                />
              )}
              <TextInputStandalone
                label="Établie le"
                name="date"
                type="date"
                value={date}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setDate(e.target.value)
                }
              />
            </div>
            <SearchInput
              placeholder="Rechercher une clause (bruit, ménage, charges…)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {isLoading && <Text>Chargement des clauses…</Text>}
            {!isLoading && query && visibleBlocks.length === 0 && (
              <Callout variant="neutral">
                Aucune clause ne correspond à « {search} ».
              </Callout>
            )}
            <Accordion
              multiple
              value={
                query ? visibleBlocks.map((block) => block.number) : openBlocks
              }
              onValueChange={(value) => {
                if (!query) setOpenBlocks(value);
              }}
            >
              {visibleBlocks.map((block) => {
                const checkedCount = block.clauses.filter(isChecked).length;
                return (
                  <AccordionItem
                    key={block.number}
                    value={block.number}
                    title={
                      <span className={styles.blockTitle}>
                        <Text as="span" weight="bold">
                          {block.number}. {block.title}
                        </Text>
                        <Status
                          as="span"
                          color={checkedCount ? "info" : "neutral"}
                          text={`${checkedCount} / ${block.clauses.length}`}
                        />
                      </span>
                    }
                  >
                    <div className={styles.clauses}>
                      {block.clauses.map((clause) => (
                        <div key={clause.id} className={styles.clause}>
                          <CheckboxStandalone
                            checked={isChecked(clause)}
                            onChange={(e) =>
                              setOverrides((current) => ({
                                ...current,
                                [clause.id]: e.target.checked,
                              }))
                            }
                          >
                            <Text as="span" className={styles.clauseTitle}>
                              {clause.title}
                              {clause.recommended && (
                                <Status
                                  as="span"
                                  color="success"
                                  text="Recommandée"
                                />
                              )}
                              {clause.compliance && (
                                <Status
                                  as="span"
                                  color="info"
                                  text="Conformité"
                                />
                              )}
                            </Text>
                          </CheckboxStandalone>
                          <Text variant="deci" className={styles.clauseDesc}>
                            {clause.description}
                          </Text>
                          {clause.variants.length > 0 && isChecked(clause) && (
                            <div className={styles.variants}>
                              {clause.variants.map((variant) => (
                                <RadioStandalone
                                  key={variant.key}
                                  name={`variant-${clause.id}`}
                                  value={variant.key}
                                  label={variant.label}
                                  checked={
                                    (variants[clause.id] ??
                                      clause.default_variant ??
                                      clause.variants[0].key) === variant.key
                                  }
                                  onChange={() =>
                                    setVariants((current) => ({
                                      ...current,
                                      [clause.id]: variant.key,
                                    }))
                                  }
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </AccordionItem>
                );
              })}
            </Accordion>
            <div className={styles.custom}>
              <Text weight="bold">Ajouter vos propres articles</Text>
              <Text>
                Besoin d'une règle spécifique à votre colocation ? Ajoutez
                autant d'articles que vous voulez, chacun avec un titre et une
                description.
              </Text>
              {articles.map((article, index) => (
                <div key={article.key} className={styles.article}>
                  <div className={styles.articleHead}>
                    <Text variant="deci" weight="bold">
                      Article libre {index + 1}
                    </Text>
                    <IconButton
                      icon={IconTrash}
                      label="Supprimer l'article"
                      size="sm"
                      onClick={() =>
                        setArticles((current) =>
                          current.filter((a) => a.key !== article.key),
                        )
                      }
                    />
                  </div>
                  <TextInputStandalone
                    name={`article-title-${article.key}`}
                    placeholder="Titre de l'article (ex : Utilisation du jardin)"
                    value={article.title}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      updateArticle(article.key, { title: e.target.value })
                    }
                  />
                  <textarea
                    className={styles.textarea}
                    placeholder="Description de la règle…"
                    rows={3}
                    value={article.body}
                    onChange={(e) =>
                      updateArticle(article.key, { body: e.target.value })
                    }
                  />
                </div>
              ))}
              <Button
                type="button"
                variant="secondary"
                icon={IconPlus}
                onClick={addArticle}
                className={styles.addArticle}
              >
                Ajouter un article
              </Button>
            </div>
            <Callout variant="danger">{error}</Callout>
          </ModalDialogBody>
          <ModalDialogFooter
            start={
              <Text color="muted">
                {selectedCount} {selectedCount > 1 ? "clauses" : "clause"}
              </Text>
            }
          >
            <Button
              type="button"
              variant="secondary"
              onClick={() => handleOpenChange(false)}
            >
              Annuler
            </Button>
            <Button
              type="button"
              onClick={handleGenerate}
              isLoading={isGenerating}
              disabled={isGenerating || !canGenerate}
              requiresWrite
              profileId={propertyFolder.id_profile_nom_owner}
            >
              Générer la charte
            </Button>
          </ModalDialogFooter>
        </>
      )}
    </ModalDialog>
  );
}
